// Semáforo de cobro por mes, como en Kydemy (pedido de Karla 29-jul-2026):
// un chip por mes del semestre (ago→dic / ene→jun; julio: anterior·actual·siguiente) para saber
// de un golpe quién no ha pagado.
// Su frase: «si debe sale en rojito y les digo a las chicas: para esta fecha no debe haber rojos».
import { useState, MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { MonthPayState } from '../api';

const MES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

// Karla 29-sep-2026: «que ese sea el estado natural: ROJO cuando deba y VERDE cuando esté pagado».
// El verde era un tinte pálido que sobre la foto no se distinguía del gris; ahora pagado y debe
// son SÓLIDOS y del mismo peso, y lo que no cuenta (mes por venir, no era alumno) va en blanco/gris
// discreto — así los 5 chips del semestre se leen de un barrido.
const CLS: Record<MonthPayState['status'], string> = {
  paid: 'bg-emerald-600 text-white ring-emerald-700/40',
  partial: 'bg-amber-500 text-white ring-amber-600/40',
  // El rojo tiene que gritar: es el que ella busca de un barrido ("no debe haber rojos").
  due: 'bg-red-600 text-white ring-red-700/40',
  pending: 'bg-white/90 text-slate-500 ring-slate-300 dark:bg-slate-800/85 dark:text-slate-300 dark:ring-slate-600',
  na: 'bg-slate-200/80 text-slate-400 ring-slate-300/60 dark:bg-slate-700/70 dark:text-slate-400 dark:ring-slate-600/60',
};

function money(n: number) {
  return n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 });
}

const nombreMes = (ym: string) => {
  const [y, mm] = ym.split('-');
  return `${MES[Number(mm) - 1]} ${y}`;
};

/** Detalle del mes: cuánto entró, cuánto se esperaba y por qué está de ese color. */
function detalle(m: MonthPayState) {
  const cur = new Date().toISOString().slice(0, 7);
  const cerrado = m.month < cur, enCurso = m.month === cur;
  const falta = Math.max(0, (m.expected || 0) - (m.paid || 0));
  switch (m.status) {
    case 'paid':
      return { titulo: 'Pagado', color: 'text-emerald-300',
        lineas: [m.paid ? `Entró ${money(m.paid)}` : null,
                 // El motivo manda sobre la frase genérica: un mes cobrado al 65% por cierre de
                 // ciclo está pagado y en verde, pero hay que poder leer POR QUÉ entró menos.
                 m.note || (cerrado ? 'Mes cerrado: no quedó nada a deber.' : null)].filter(Boolean) as string[] };
    case 'partial':
      return { titulo: 'Abonó una parte', color: 'text-amber-300',
        lineas: [`Abonó ${money(m.paid)} de ${money(m.expected)}`, `Falta ${money(falta)}`, m.note || null,
                 enCurso ? 'Es el mes en curso: se mide contra la mensualidad completa.' : null].filter(Boolean) as string[] };
    case 'due':
      return { titulo: 'Sin pago', color: 'text-red-300',
        lineas: [m.expected ? `Debe ${money(m.expected)}` : 'No hay pago registrado',
                 cerrado ? 'Mes ya cerrado.' : null].filter(Boolean) as string[] };
    case 'pending':
      return { titulo: 'Todavía no corre', color: 'text-slate-300',
        lineas: ['Se pinta rojo hasta que empiece el mes y siga sin pago.'] };
    default:
      // GRIS en un mes ya cerrado = no hay rastro de que fuera alumno ese mes (alta posterior o
      // baja de fin de ciclo). No es lo mismo que "no tiene plan" y no debe leerse como deuda.
      // Con motivo = el cargo del mes existió y se cerró sin cobro (exento, baja, alta de fin de mes).
      if (cerrado && m.note) return { titulo: 'Sin cobro este mes', color: 'text-slate-300', lineas: [m.note] };
      return { titulo: cerrado ? 'No debe este mes' : 'Sin plan de cobro', color: 'text-slate-300',
        lineas: [cerrado ? 'No se le generó mensualidad ni quedó adeudo (alta posterior, exento o baja).'
                         : 'Este alumno no tiene mensualidad asignada.'] };
  }
}

export default function MonthChips({ months, className = '', compact = false }: { months?: MonthPayState[]; className?: string; compact?: boolean }) {
  const [tip, setTip] = useState<{ m: MonthPayState; x: number; y: number; arriba: boolean } | null>(null);
  if (!months?.length) return null;

  const seguir = (m: MonthPayState) => (e: MouseEvent) => {
    const ALTO = 130;
    const arriba = e.clientY + ALTO + 20 > window.innerHeight;
    setTip({ m, x: Math.max(8, Math.min(e.clientX + 16, window.innerWidth - 248)), y: arriba ? e.clientY - 10 : e.clientY + 16, arriba });
  };

  const d = tip ? detalle(tip.m) : null;
  return (
    // data-no-tip: mientras el cursor anda sobre los meses, el tooltip del ALUMNO se calla —
    // si no, se tapan entre ellos y no se alcanza a leer cuánto abonó (Carlos, 30-jul).
    <div data-no-tip className={`flex shrink-0 ${compact ? 'flex-wrap gap-0.5' : 'gap-1'} ${className}`}>
      {months.map((m) => (
        <span key={m.month}
          onMouseEnter={seguir(m)} onMouseMove={seguir(m)} onMouseLeave={() => setTip(null)}
          className={`cursor-help rounded font-bold uppercase shadow-sm ring-1 ${compact ? 'px-1 py-px text-[9px]' : 'px-1.5 py-0.5 text-[10px]'} ${CLS[m.status]}`}>
          {MES[Number(m.month.split('-')[1]) - 1]}
        </span>
      ))}
      {tip && d && createPortal(
        <div role="tooltip"
          style={{ left: tip.x, top: tip.y, transform: tip.arriba ? 'translateY(-100%)' : undefined }}
          className="pointer-events-none fixed z-[1000] w-[240px] rounded-xl bg-slate-800 px-3 py-2.5 text-[12.5px] leading-snug text-white shadow-xl ring-1 ring-black/20">
          <div className="font-semibold capitalize">{nombreMes(tip.m.month)}</div>
          <div className={`mt-0.5 font-semibold ${d.color}`}>{d.titulo}</div>
          {d.lineas.map((l, i) => <div key={i} className="mt-0.5 text-slate-300">{l}</div>)}
        </div>, document.body)}
    </div>
  );
}
